// Reads of the Inference pages that are not InferenceEndpoint lists: the model catalog and a request's receipt come
// from the inference data plane, which answers the console's user token on its own origin (the console is one of its
// CORS origins). Endpoints themselves are control-plane objects and use useKList and useKObject.
import { NodusClient } from "@nodus/api";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useRuntime } from "../../app/runtime";
import { endpointBaseURL, inferenceOrigin, type ModelList, type Receipt, type Scope } from "./model";

/** A client for the data plane: the console's credential and refresh, the inference origin as the base. */
export function useInferenceClient(): NodusClient {
  const { client, config } = useRuntime();
  const origin = inferenceOrigin(config);
  return React.useMemo(() => new NodusClient({ ...client.config, baseURL: origin }), [client, origin]);
}

/** The base URL of one endpoint, for the pages and snippets that show it. */
export function useEndpointBaseURL(scope: Scope, name: string): string {
  const { config } = useRuntime();
  return endpointBaseURL(config, scope, name);
}

/** The data plane's OpenAI-compatible base URL, as a catalog model's snippets call it. */
export function useInferenceBaseURL(): string {
  const { config } = useRuntime();
  return `${inferenceOrigin(config) || window.location.origin}/v1`;
}

/** The catalog models the caller may invoke, with Indra (the routed model) among them. */
export function useModels(scope: Scope) {
  const client = useInferenceClient();
  return useQuery({
    queryKey: [scope.org, scope.project, "inference", "models"],
    queryFn: () => client.get<ModelList>("/v1/models", { org: scope.org }),
    staleTime: 5 * 60_000,
  });
}

/** The receipt of a request id; the caller decides when to look. */
export function useReceiptLookup(scope: Scope) {
  const client = useInferenceClient();
  return (id: string) => client.get<Receipt>(`/v1/requests/${encodeURIComponent(id)}`, { org: scope.org });
}
